import React, { ReactElement, useMemo } from 'react';
import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Button, Flex, Section, Typography } from 'components';

import 'swiper/swiper.scss';
import 'swiper/components/navigation/navigation.scss';
import './styles.scss';

import { DashboardAreaColorEnum, DashboardVisualizationTypeEnum, useSheets } from 'features/dashboard-new';
import { NavigationWrapper, NavigationButton, NavigationIcon } from './styles';
import { ProductsCardNew } from 'features/products';
import { ProductJourneyDashboardAreaResponse } from 'features/journeys/types/ProductJourneyDashboardAreaResponse';
import { JourneyAreaDetailsCard } from '..';
import { Link, useParams } from 'react-router-dom';
import { createUrl } from 'app/utils';

SwiperCore.use([Navigation]);

interface AreaCarouselProps {
  area: ProductJourneyDashboardAreaResponse;
  productEnrolled?: boolean;
}

const AreaCarousel = ({ area, productEnrolled = true }: AreaCarouselProps): ReactElement => {
  const { productSlug } = useParams();

  const { sheetTheme } = useSheets();

  const theme = sheetTheme(DashboardVisualizationTypeEnum.Standard, DashboardAreaColorEnum.Dark);

  const nextRef = React.useRef<HTMLDivElement>(null);
  const prevRef = React.useRef<HTMLDivElement>(null);

  const showAreaDetailsCard = useMemo((): boolean => {
    if (area?.videoProvider && area?.externalVideoProviderIdOrUrl) return true;
    if (area?.videoTitle || area?.videoDescription) return true;
    if (area?.journeyAreaDownloads?.length) return true;
    return false;
  }, [area]);

  return (
    <>
      <Flex id={`dashboard-area-${area.uuid}`} className="dashboard-area" flexDirection="column" flexGrow={0} bg={theme.sheetBg}>
        <Section flexDirection="column" flexGrow={0}>
          <Flex maxWidth={{ m: 280 - 48, t: 580 - 48, d: 1180 - 48 }} mb={5} alignItems="flex-end" justifyContent={{ d: 'space-between' }}>
            <Flex flexDirection="column">
              <Typography as="h2" type="type6" fontWeight="bold" color={theme.titleColor}>
                {area.name}
              </Typography>
            </Flex>

            <Flex alignItems="center" flexGrow={0}>
              {productEnrolled ? (
                <Link to={createUrl('journeys/area', [productSlug, area.uuid])}>
                  <Button color="primary_400" colorHover="primary_200" bg="transparent" p={0} height="inherit">
                    Ver tudo
                  </Button>
                </Link>
              ) : null}

              <NavigationWrapper ml={3} mt={{ m: 3, d: 0 }}>
                <NavigationButton {...theme} ref={prevRef}>
                  <NavigationIcon icon="chevronLeft" />
                </NavigationButton>

                <NavigationButton {...theme} ref={nextRef}>
                  <NavigationIcon icon="chevronRight" />
                </NavigationButton>
              </NavigationWrapper>
            </Flex>
          </Flex>

          <Swiper
            loop={false}
            autoplay={{
              delay: 1000,
            }}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              640: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 4,
              },
            }}
            spaceBetween={20}
            navigation={{
              prevEl: prevRef.current ? prevRef.current : undefined,
              nextEl: nextRef.current ? nextRef.current : undefined,
            }}
            onInit={(swiper) => {
              swiper.navigation.init();

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // eslint-disable-next-line no-param-reassign
              swiper.params.navigation.prevEl = prevRef.current;

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // eslint-disable-next-line no-param-reassign
              swiper.params.navigation.nextEl = nextRef.current;

              swiper.navigation.update();
            }}
            className="sheet-carousel"
          >
            {showAreaDetailsCard && (
              <SwiperSlide>
                <JourneyAreaDetailsCard area={area} />
              </SwiperSlide>
            )}

            {area.cards.map((card, index) => {
              return (
                <SwiperSlide key={`sheet-carousel${index.toString()}-${card.uuid}`}>
                  <ProductsCardNew
                    card={card}
                    dashboardAreaColor={DashboardAreaColorEnum.Dark}
                    dashboardVisualizationType={DashboardVisualizationTypeEnum.Standard}
                    productEnrolled={productEnrolled}
                    journeyProductSlug={productSlug}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Section>
      </Flex>
    </>
  );
};

export default AreaCarousel;
